import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useFormContext, Controller } from 'react-hook-form';
import { RadioGroup } from '@base/form/radio-group';
import { SearchableDropdown } from '@base/form/searchable-dropdown';
import { CheckboxTextInput } from '@base/form/checkbox-text-input';
import { ImageUploader } from '@base/form/image-uploader';
import { useDefectCatalog, useComponentCatalog } from '@base/state/use-catalog';
import { useNavigationStore } from '../router/navigation-store';
import type { ImageResponse } from '@core/types/ticket';

interface DefectLineFormProps {
  lineNumber: number;
  onDelete: () => void;
  isOnlyLine: boolean;
  fieldName: string; // e.g., "defectLines.0"
  disabled?: boolean;
  existingImages?: ImageResponse[];
  onExistingImageRemove?: (imageIndex: number) => void;
}

export function DefectLineForm({
  lineNumber,
  onDelete,
  isOnlyLine,
  fieldName,
  disabled = false,
  existingImages,
  onExistingImageRemove,
}: DefectLineFormProps) {
  const { control, watch, setValue } = useFormContext();
  const urlParams = useNavigationStore((state) => state.urlParams);
  const resolvedItemCode = useNavigationStore((state) => state.resolvedItemCode);
  const itemCode = resolvedItemCode || urlParams?.item_code || '';

  // Watch line type to show/hide component dropdown
  const lineType = watch(`${fieldName}.lineType`);

  // Watch selected component for component-level defect catalog
  const selectedComponentCode = watch(`${fieldName}.componentItemCode`);

  // Fetch machine-level catalog (defects + components list)
  const { data: machineCatalog } = useDefectCatalog(itemCode);

  // Fetch component-specific defect catalog when a component is selected
  const { data: componentCatalog } = useComponentCatalog(
    lineType === 'Component' && selectedComponentCode ? selectedComponentCode : ''
  );

  // Watch checkbox states for mutual exclusion
  const isOtherDefect = watch(`${fieldName}.isOtherDefect`);
  const isOtherCause = watch(`${fieldName}.isOtherCause`);

  // Components list always comes from machine catalog
  const componentOptions = (machineCatalog?.components ?? []).map((c) => ({
    code: c.item_code,
    name: c.item_name,
  }));

  // Defects depend on lineType:
  //   Machine → defects from machine catalog
  //   Component → defects from component-specific catalog (once a component is selected)
  const activeCatalogDefects =
    lineType === 'Component' && selectedComponentCode
      ? componentCatalog?.defects
      : machineCatalog?.defects;

  const defectOptions = (activeCatalogDefects ?? []).map((d) => ({
    code: d.defect_code,
    name: d.defect_name,
    severity: d.severity,
  }));

  // Derive causes from the selected defect in the active catalog
  const selectedDefectCode = watch(`${fieldName}.marketDefect`);
  const selectedDefect = activeCatalogDefects?.find((d) => d.defect_code === selectedDefectCode);
  const causeOptions = (selectedDefect?.causes ?? []).map((c) => ({
    code: c.cause_code,
    name: c.cause_name,
  }));

  // Clear dependent defect & cause fields.
  // Called ONLY from user-driven onChange handlers (not from programmatic resets)
  // so that EditPage pre-population is preserved.
  const clearDependentFields = () => {
    setValue(`${fieldName}.marketDefect`, null);
    setValue(`${fieldName}.marketDefectCause`, null);
    setValue(`${fieldName}.isOtherDefect`, false);
    setValue(`${fieldName}.isOtherCause`, false);
    setValue(`${fieldName}.otherDefectDescription`, '');
    setValue(`${fieldName}.otherCauseDescription`, '');
  };

  // Handle user changing lineType via radio button
  const handleLineTypeChange = (newLineType: string) => {
    const currentLineType = watch(`${fieldName}.lineType`);
    setValue(`${fieldName}.lineType`, newLineType);
    if (currentLineType !== newLineType) {
      clearDependentFields();
    }
  };

  // Handle user changing selected component via dropdown
  const handleComponentChange = (newComponentCode: string | null) => {
    const currentComponent = watch(`${fieldName}.componentItemCode`);
    setValue(`${fieldName}.componentItemCode`, newComponentCode);
    if (currentComponent !== newComponentCode) {
      clearDependentFields();
    }
  };

  // Handle checkbox toggle for Defect — also sync Cause
  const handleDefectToggle = (checked: boolean) => {
    setValue(`${fieldName}.isOtherDefect`, checked);
    if (checked) {
      // Custom defect → custom cause must also be selected
      setValue(`${fieldName}.marketDefect`, null);
      setValue(`${fieldName}.isOtherCause`, true);
      setValue(`${fieldName}.marketDefectCause`, null);
    } else {
      // Back to dropdown → reset both custom fields
      setValue(`${fieldName}.otherDefectDescription`, '');
      setValue(`${fieldName}.isOtherCause`, false);
      setValue(`${fieldName}.otherCauseDescription`, '');
    }
  };

  // Handle checkbox toggle for Cause (independent toggle)
  const handleCauseToggle = (checked: boolean) => {
    setValue(`${fieldName}.isOtherCause`, checked);
    if (checked) {
      setValue(`${fieldName}.marketDefectCause`, null);
    } else {
      setValue(`${fieldName}.otherCauseDescription`, '');
    }
  };

  return (
    <Card sx={{ mb: 2, position: 'relative' }}>
      <CardContent sx={{ px: 4, py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Lỗi #{lineNumber}
          </Typography>
          <IconButton
            onClick={onDelete}
            disabled={disabled || isOnlyLine}
            color="error"
            size="small"
            title={isOnlyLine ? 'Không thể xóa dòng cuối cùng' : 'Xóa dòng lỗi này'}
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        {/* Line Type Radio Buttons */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
            Loại lỗi *
          </Typography>
          <Controller
            name={`${fieldName}.lineType`}
            control={control}
            defaultValue="Machine"
            render={({ field }) => (
              <RadioGroup
                options={[
                  { value: 'Machine', label: 'Lỗi máy' },
                  { value: 'Component', label: 'Lỗi linh kiện' },
                ]}
                value={field.value}
                onChange={handleLineTypeChange}
                disabled={disabled}
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Conditional Component Dropdown */}
        {lineType === 'Component' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
              Linh kiện *
            </Typography>
            <Controller
              name={`${fieldName}.componentItemCode`}
              control={control}
              render={({ field, fieldState }) => (
                <SearchableDropdown
                  options={componentOptions}
                  value={field.value}
                  onChange={handleComponentChange}
                  placeholder="Chọn linh kiện..."
                  disabled={disabled}
                  error={!!fieldState.error}
                />
              )}
            />
          </Box>
        )}

        {/* Defect CheckboxTextInput */}
        <Box sx={{ mb: 3 }}>
          <Controller
            name={`${fieldName}.marketDefect`}
            control={control}
            render={({ field: defectField, fieldState }) => (
              <Controller
                name={`${fieldName}.otherDefectDescription`}
                control={control}
                render={({ field: textField }) => (
                  <CheckboxTextInput
                    checkboxLabel="Lỗi không có trong danh sách"
                    dropdownOptions={defectOptions}
                    dropdownValue={defectField.value}
                    dropdownPlaceholder="Chọn lỗi..."
                    textValue={textField.value || ''}
                    textPlaceholder="Nhập mô tả lỗi..."
                    dropdownDisabled={isOtherDefect}
                    textDisabled={!isOtherDefect}
                    disabled={disabled}
                    error={fieldState.error?.message}
                    onToggle={handleDefectToggle}
                    onDropdownChange={defectField.onChange}
                    onTextChange={textField.onChange}
                  />
                )}
              />
            )}
          />
        </Box>

        {/* Cause CheckboxTextInput */}
        <Box sx={{ mb: 2 }}>
          <Controller
            name={`${fieldName}.marketDefectCause`}
            control={control}
            render={({ field: causeField, fieldState }) => (
              <Controller
                name={`${fieldName}.otherCauseDescription`}
                control={control}
                render={({ field: textField }) => (
                  <CheckboxTextInput
                    checkboxLabel="Nguyên nhân không có trong danh sách"
                    dropdownOptions={causeOptions}
                    dropdownValue={causeField.value}
                    dropdownPlaceholder="Chọn nguyên nhân..."
                    textValue={textField.value || ''}
                    textPlaceholder="Nhập nguyên nhân..."
                    dropdownDisabled={isOtherCause}
                    textDisabled={!isOtherCause}
                    disabled={disabled}
                    checkboxDisabled={isOtherDefect}
                    error={fieldState.error?.message}
                    onToggle={handleCauseToggle}
                    onDropdownChange={causeField.onChange}
                    onTextChange={textField.onChange}
                  />
                )}
              />
            )}
          />
        </Box>

        {/* Image Uploader — unified grid for existing + new images */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
            Hình ảnh (Tối đa 5 ảnh)
          </Typography>
          <Controller
            name={`${fieldName}.images`}
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <ImageUploader
                maxImages={5}
                images={field.value || []}
                onImagesChange={field.onChange}
                disabled={disabled}
                existingImages={existingImages}
                onExistingImageRemove={onExistingImageRemove}
              />
            )}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
